/**
 * ProjectForm — create / edit form for projects. Hosted in three places: the
 * project rail's "Project settings" section (edit mode, accountId known from
 * the loaded project), and the top-nav Create menu's drawer (create mode, no
 * accountId in scope — the form renders its own required Account select).
 * On edit it fetches the full project (a dashboard row would only be a lean
 * RAG entry) before prefilling. Token system only.
 */
import { useState } from 'react';
import { useProject, useCreateProject, useUpdateProject } from '@/api/project';
import type { ProjectCreatePayload, ProjectUpdatePayload } from '@/api/project';
import { useConfigOptions } from '@/api/config';
import { useMembers } from '@/api/member';
import { useAccounts } from '@/api/account';
import type { ProjectDetail } from '@/types/workItem';
import type { SprintMode } from '@/types/sprint';
import { BacklogSchemaBuilder, draftTrio } from '@/components/backlogSchema/BacklogSchemaBuilder';
import type { BuilderField } from '@/components/backlogSchema/FieldBuilderRow';

const SPRINT_MODE_OPTIONS: { value: SprintMode; label: string }[] = [
  { value: 'OFF', label: 'Off' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'AUTO', label: 'Auto' },
];

// ---------------------------------------------------------------------------
// Shared control kit — the app-wide form vocabulary (DESIGN-SPEC §2.16 / §2.8).
// ProjectForm is the kit's source of truth; sibling forms (NewTicketForm,
// ConfigListCard, MembersSection, HolidaysSection, AccountsPage,
// DeleteAccountDialog) import these instead of re-declaring their own copies.
// Anatomy: borderless luminance-stepped elevation — inputs are wells that step
// DOWN (--surface-inset), form panels step UP (--surface-2), every button is a
// pill, exactly one accent primary per screen (the form's submit).
// ---------------------------------------------------------------------------
/* eslint-disable react-refresh/only-export-components --
   the spec (§3.2 S2) codifies the control kit as exported style constants in
   ProjectForm, the de-facto kit file; losing Fast Refresh here is accepted. */

/** Field label — 12/500 sentence case, 6px above its field (§2.16).
 *  Colour: --text-muted (not --text-low/--text-subtle) — the only neutral that
 *  clears the 4.5:1 text bar on --surface-2 form panels in BOTH themes. */
export const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 500,
  color: 'var(--text-muted)', display: 'block', marginBottom: 6,
};
/** Required-field asterisk — amber-text suffix on the label (§2.16). */
export const reqMarkStyle: React.CSSProperties = { color: 'var(--sem-amber-text)' };
/** Input/select well — steps DOWN from its parent, no stroke at rest, r12; pair
 *  with className="focus-ring" for the accent focus ring (§2.16). */
export const fieldStyle: React.CSSProperties = {
  width: '100%', height: 36, padding: '0 12px', border: 'none',
  borderRadius: 'var(--radius-inset)', background: 'var(--surface-inset)', color: 'var(--text)',
  fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', outline: 'none', boxSizing: 'border-box',
};
/** Errored field — 1px --sem-red ring layered on the well (§2.16); spread after
 *  fieldStyle/textareaStyle when the field has a validation message. */
export const fieldErrorStyle: React.CSSProperties = {
  boxShadow: '0 0 0 1px var(--sem-red)',
};
/** Multiline well — same anatomy, free height (§2.16). */
export const textareaStyle: React.CSSProperties = {
  ...fieldStyle, height: 'auto', minHeight: 80, padding: '8px 12px', resize: 'vertical',
};
/** Native checkbox — 16px, accent check via accent-color (§2.16).
 *  DEPRECATED: prefer <KitCheckbox> below — a native checkbox's UNCHECKED state
 *  renders as a UA-white square on the dark theme (accent-color only tints the
 *  checked state), which reads as a stray selection-inversion fill (§1.8 #3). */
export const checkboxStyle: React.CSSProperties = {
  width: 16, height: 16, accentColor: 'var(--brand)', cursor: 'pointer', flexShrink: 0,
};
/** Inline field/form error — 12px below the field, --sem-red-text (§2.16). */
export const errorTextStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--sem-red-text)',
  display: 'block', marginTop: 6,
};
/** Ledger header cell — 12/500 sentence-case --text-low (§2.15, §1.5); spread
 *  onto <th> to override the legacy `.ledger` uppercase-mono idiom inline
 *  (index.css is owned elsewhere this batch). */
export const theadCellStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 500,
  letterSpacing: 0, textTransform: 'none', color: 'var(--text-low)',
};
/** Form sub-panel — for forms nested inside a section card: steps UP one rung so
 *  its wells still read as wells (§2.11). */
export const formPanelStyle: React.CSSProperties = {
  background: 'var(--surface-2)', borderRadius: 'var(--radius-md)', padding: '18px 20px',
};
/** Panel/form title — 17/600 (§1.5 section/panel role). */
export const formTitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)', fontWeight: 600,
  color: 'var(--text)', margin: '0 0 16px',
};
/** Primary CTA — the screen's single accent pill, h40 (§2.8 primary tier). */
export const btnPrimaryStyle: React.CSSProperties = {
  height: 40, padding: '0 20px', border: 'none', borderRadius: 'var(--radius-pill)',
  background: 'var(--btn-primary-bg)', color: 'var(--btn-primary-fg)',
  fontFamily: 'var(--font-sans)', fontSize: 'var(--text-base)', fontWeight: 600,
  cursor: 'pointer', boxSizing: 'border-box', whiteSpace: 'nowrap',
};
/** Secondary/tonal pill — neutral fill, no stroke (§2.8). Fill is --surface-4, NOT --surface-2:
 *  light --surface-2 (#f1f1ee) is ~1.01:1 against the paper canvas (#f2f2ef), so a borderless
 *  tonal button renders as bare floating text. --surface-4 is the ladder's sanctioned "highest
 *  neutral" tier — the same substitution tokens.css already makes for --icon-circle-bg in the
 *  light theme; dark keeps a visible charcoal step either way. */
export const btnTonalStyle: React.CSSProperties = {
  height: 36, padding: '0 18px', border: 'none', borderRadius: 'var(--radius-pill)',
  background: 'var(--surface-4)', color: 'var(--text)',
  fontFamily: 'var(--font-sans)', fontSize: 'var(--text-base)', fontWeight: 500,
  cursor: 'pointer', boxSizing: 'border-box', whiteSpace: 'nowrap',
};
/** Ghost text pill — transparent Cancel-tier action (§2.8). */
export const btnGhostStyle: React.CSSProperties = {
  ...btnTonalStyle, background: 'transparent', color: 'var(--text-muted)',
};
/** Destructive confirm — solid health-red pill; --text-inverted is light-on-red
 *  in the light theme and near-black-on-coral in dark (never raw #fff) (§2.8). */
export const btnDangerStyle: React.CSSProperties = {
  ...btnPrimaryStyle, background: 'var(--health-red)', color: 'var(--text-inverted)',
};
/* eslint-enable react-refresh/only-export-components */

/**
 * KitCheckbox — the kit's 16px checkbox (§2.16): r4 well with a ghost ring at
 * rest, solid accent fill + --on-accent mark when checked. Custom-drawn
 * (appearance: none) because a native checkbox's unchecked state renders as a
 * UA-white square on the dark theme — white fill is reserved for selection
 * inversion (§1.8 #3). Keeps a real <input type="checkbox"> so wrapping-label
 * association, keyboard toggling and form semantics are unchanged.
 */
export function KitCheckbox({ checked, onChange, disabled, style, ...rest }: {
  checked: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  style?: React.CSSProperties;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'checked' | 'onChange' | 'disabled' | 'style' | 'type'>) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', width: 16, height: 16, flexShrink: 0, ...style }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="focus-ring"
        style={{
          appearance: 'none', WebkitAppearance: 'none', margin: 0, width: 16, height: 16,
          borderRadius: 4, boxSizing: 'border-box',
          border: checked ? '1px solid transparent' : '1px solid var(--ring-ghost)',
          background: checked ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
          transition: 'background var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out)',
        }}
        {...rest}
      />
      {checked && (
        <svg
          aria-hidden="true"
          width="10" height="10" viewBox="0 0 24 24" fill="none"
          stroke="var(--btn-primary-fg)" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', top: 3, left: 3, pointerEvents: 'none' }}
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </span>
  );
}

interface ProjectFormValues {
  name: string;
  code: string;
  type: string;
  ownerMemberId: string;
  startDate: string;
  targetEndDate: string;
  description: string;
  sprintMode: SprintMode;
  sprintCadenceWeeks: number;
  /** Create-mode only in effect (the "Backlog table" section only renders
   *  when !initial) — carried on ProjectFormValues like every other field
   *  rather than a separate useState, for the same single-state-object
   *  convention the rest of the form already follows. */
  recordPrefix: string;
}

/** Account picker wiring — present only when the host has no accountId of its own. */
interface AccountPicker {
  accounts: { id: string; name: string }[];
  value: string;
  onChange: (id: string) => void;
}

function ProjectFormInner({
  accountId, accountPicker, initial, onDone, onCancel, embedded,
}: {
  accountId: string;
  accountPicker?: AccountPicker;
  initial: ProjectDetail | null;
  onDone: (project?: ProjectDetail) => void;
  onCancel?: () => void;
  /** True when hosted inside a drawer (§2.18): the drawer header already
   *  carries the title and the drawer body is the panel — render fields as
   *  bare inset wells with no duplicate heading or nested raised card. */
  embedded?: boolean;
}) {
  const { data: projectTypeOptions = [] } = useConfigOptions('project_type');
  const defaultType = projectTypeOptions[0]?.value ?? '';
  const [values, setValues] = useState<ProjectFormValues>(
    initial
      ? {
          name: initial.name,
          code: initial.code,
          type: initial.type ?? defaultType,
          ownerMemberId: initial.ownerMemberId ?? '',
          startDate: initial.startDate ? initial.startDate.slice(0, 10) : '',
          targetEndDate: initial.targetEndDate ? initial.targetEndDate.slice(0, 10) : '',
          description: initial.description ?? '',
          sprintMode: initial.sprintMode ?? 'OFF',
          sprintCadenceWeeks: initial.sprintCadenceWeeks ?? 2,
          recordPrefix: initial.recordPrefix ?? 'CR',
        }
      : {
          name: '', code: '', type: defaultType, ownerMemberId: '', startDate: '', targetEndDate: '', description: '',
          sprintMode: 'OFF', sprintCadenceWeeks: 2, recordPrefix: 'CR',
        },
  );
  // Create-mode-only local draft — mirrors the create/edit split every other
  // field already follows (kept out of ProjectFormValues since it's a
  // complex array, not a scalar). Always initialized (hooks can't be
  // conditional); only rendered/consumed when !initial.
  const [backlogFields, setBacklogFields] = useState<BuilderField[]>(draftTrio());
  const [formError, setFormError] = useState<string | null>(null);
  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject();
  const { data: members = [], isError: membersError } = useMembers();
  const isPending = createMutation.isPending || updateMutation.isPending;

  function set<K extends keyof ProjectFormValues>(k: K, v: ProjectFormValues[K]) {
    setValues((prev) => ({ ...prev, [k]: v }));
  }

  const valid = values.name.trim().length > 0 && values.code.trim().length > 0 && accountId.trim().length > 0;

  function onError(err: unknown) {
    const code = (err as { code?: string })?.code;
    setFormError(
      code === 'DUPLICATE_PROJECT_CODE'
        ? 'That project code is already used in this account.'
        : code === 'MEMBER_NOT_FOUND'
          ? 'That owner could not be found.'
          : 'Could not save the project. Please try again.',
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setFormError(null);
    const owner = values.ownerMemberId.trim() || null;
    const common = {
      name: values.name.trim(),
      code: values.code.trim(),
      type: values.type,
      description: values.description.trim() || null,
      ownerMemberId: owner,
      startDate: values.startDate || null,
      targetEndDate: values.targetEndDate || null,
    };
    if (initial) {
      // sprintMode/sprintCadenceWeeks are update-only fields — CreateProjectRequest
      // on the backend has no such fields, a new project always starts OFF/2.
      const payload: ProjectUpdatePayload = {
        ...common,
        sprintMode: values.sprintMode,
        sprintCadenceWeeks: values.sprintCadenceWeeks,
      };
      updateMutation.mutate({ id: initial.id, accountId, payload }, { onSuccess: (data) => onDone(data.item), onError });
    } else {
      // The trio is excluded — the backend always injects it itself
      // (CreateProjectCommandService.persistBacklogSchema). Only the fields
      // the PM actually added are sent, in the wizard's order; when that
      // set is empty the key is omitted entirely so the backend's own
      // trio-only default applies with zero ambiguity.
      const backlogSpecs = backlogFields
        .filter((f) => !f.locked)
        .map((f) => ({
          kind: f.kind,
          systemRef: f.systemRef ?? undefined,
          label: f.label,
          dataType: f.dataType ?? undefined,
          options: f.options ?? undefined,
          required: f.required,
        }));
      const payload: ProjectCreatePayload = {
        accountId,
        ...common,
        recordPrefix: values.recordPrefix || 'CR',
        ...(backlogSpecs.length > 0 ? { backlogFields: backlogSpecs } : {}),
      };
      createMutation.mutate(payload, { onSuccess: (item) => onDone(item), onError });
    }
  }

  return (
    <div className={embedded ? undefined : 'pop-in'} style={embedded ? undefined : { ...formPanelStyle, marginBottom: 16 }}>
      {!embedded && (
        <h3 style={formTitleStyle}>
          {initial ? 'Edit project' : 'New project'}
        </h3>
      )}
      <form onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
          {accountPicker && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="pr-account" style={labelStyle}>Account<span style={reqMarkStyle}> *</span></label>
              <select
                id="pr-account"
                required
                value={accountPicker.value}
                onChange={(e) => accountPicker.onChange(e.target.value)}
                className="focus-ring"
                style={fieldStyle}
              >
                <option value="">— select an account —</option>
                {accountPicker.accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="pr-name" style={labelStyle}>Name<span style={reqMarkStyle}> *</span></label>
            <input id="pr-name" required value={values.name} onChange={(e) => set('name', e.target.value)} placeholder="Project name" className="focus-ring" style={fieldStyle} />
          </div>
          <div>
            <label htmlFor="pr-code" style={labelStyle}>Code<span style={reqMarkStyle}> *</span></label>
            <input id="pr-code" required value={values.code} onChange={(e) => set('code', e.target.value)} placeholder="PRJ-01" className="focus-ring" style={{ ...fieldStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
          <div>
            <label htmlFor="pr-type" style={labelStyle}>Type<span style={reqMarkStyle}> *</span></label>
            <select id="pr-type" value={values.type} onChange={(e) => set('type', e.target.value)} className="focus-ring" style={fieldStyle}>
              {projectTypeOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="pr-owner" style={labelStyle}>Owner</label>
            {membersError ? (
              <input id="pr-owner" value={values.ownerMemberId} onChange={(e) => set('ownerMemberId', e.target.value)} placeholder="Member ID (optional)" className="focus-ring" style={fieldStyle} />
            ) : (
              <select id="pr-owner" value={values.ownerMemberId} onChange={(e) => set('ownerMemberId', e.target.value)} className="focus-ring" style={fieldStyle}>
                <option value="">— none —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{m.displayName}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label htmlFor="pr-start" style={labelStyle}>Start date</label>
            <input id="pr-start" type="date" value={values.startDate} onChange={(e) => set('startDate', e.target.value)} className="focus-ring" style={fieldStyle} />
          </div>
          <div>
            <label htmlFor="pr-target" style={labelStyle}>Target end date</label>
            <input id="pr-target" type="date" value={values.targetEndDate} onChange={(e) => set('targetEndDate', e.target.value)} className="focus-ring" style={fieldStyle} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="pr-desc" style={labelStyle}>Description</label>
            <textarea id="pr-desc" value={values.description} onChange={(e) => set('description', e.target.value)} rows={2} placeholder="Notes…" className="focus-ring" style={textareaStyle} />
          </div>
          {/* Backlog table design — create-mode only. The wizard's draft
              builder; nothing here hits the network until Create is pressed. */}
          {!initial && (
            <div style={{ gridColumn: '1 / -1' }}>
              <h4 style={{ ...formTitleStyle, fontSize: 'var(--text-base)', margin: '4px 0 12px' }}>Backlog table</h4>
              <div style={{ maxWidth: 200, marginBottom: 14 }}>
                <label htmlFor="pr-record-prefix" style={labelStyle}>Record prefix</label>
                <input
                  id="pr-record-prefix"
                  value={values.recordPrefix}
                  onChange={(e) => set('recordPrefix', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
                  placeholder="CR"
                  className="focus-ring"
                  style={{ ...fieldStyle, fontFamily: 'var(--font-mono)' }}
                />
                <p style={{ margin: '6px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xs)', color: 'var(--text-muted)' }}>
                  Records will be numbered {values.recordPrefix || 'CR'}-1, {values.recordPrefix || 'CR'}-2…
                </p>
              </div>
              <BacklogSchemaBuilder mode="draft" fields={backlogFields} onChange={setBacklogFields} />
            </div>
          )}
          {/* Sprint mode/cadence are update-only on the backend (CreateProjectRequest
              has no such fields) — only shown once a project exists to edit. */}
          {initial && (
            <>
              <div>
                <label htmlFor="pr-sprint-mode" style={labelStyle}>Sprint mode</label>
                <select
                  id="pr-sprint-mode"
                  value={values.sprintMode}
                  onChange={(e) => set('sprintMode', e.target.value as SprintMode)}
                  className="focus-ring"
                  style={fieldStyle}
                >
                  {SPRINT_MODE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              {values.sprintMode !== 'OFF' && (
                <div>
                  <label htmlFor="pr-sprint-cadence" style={labelStyle}>Cadence (weeks)</label>
                  <input
                    id="pr-sprint-cadence"
                    type="number"
                    min={1}
                    max={4}
                    value={values.sprintCadenceWeeks}
                    onChange={(e) => set('sprintCadenceWeeks', Math.min(4, Math.max(1, Number(e.target.value) || 1)))}
                    className="focus-ring"
                    style={fieldStyle}
                  />
                </div>
              )}
            </>
          )}
        </div>
        {formError && (
          <p role="alert" style={{ ...errorTextStyle, margin: '12px 0 0' }}>
            {formError}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => (onCancel ?? onDone)()} className="pressable focus-ring" style={btnGhostStyle}>
            Cancel
          </button>
          <button type="submit" disabled={isPending || !valid} className="pressable focus-ring" style={{ ...btnPrimaryStyle, opacity: isPending || !valid ? 0.4 : 1 }}>
            {isPending ? 'Saving…' : initial ? 'Save changes' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Account-picker wrapper — mounted only when the host has no accountId to
 * pass (e.g. the top-nav Create menu, which has no project route to infer an
 * account from). Fetches the account list and holds the chosen id itself.
 */
function ProjectFormWithAccountPicker({
  initial, onDone, onCancel, embedded,
}: { initial: ProjectDetail | null; onDone: (project?: ProjectDetail) => void; onCancel?: () => void; embedded?: boolean }) {
  const { data: accounts = [] } = useAccounts();
  const [accountId, setAccountId] = useState('');
  return (
    <ProjectFormInner
      accountId={accountId}
      accountPicker={{ accounts, value: accountId, onChange: setAccountId }}
      initial={initial}
      onDone={onDone}
      onCancel={onCancel}
      embedded={embedded}
    />
  );
}

/**
 * On edit, fetch the full project first (the dashboard row is a lean RAG entry
 * missing type/description/owner/dates), then render the form prefilled.
 * `accountId` is optional: when omitted, the form renders its own required
 * Account select above the other fields instead of assuming one.
 */
export function ProjectForm({
  accountId, editId, onDone, onCancel, embedded,
}: {
  accountId?: string;
  editId: string | null;
  onDone: (project?: ProjectDetail) => void;
  onCancel?: () => void;
  /** Hosted inside a drawer — no duplicate heading / nested panel (§2.18). */
  embedded?: boolean;
}) {
  const { data: detail, isLoading } = useProject(editId ?? '');

  if (editId && (isLoading || !detail)) {
    return (
      <div className="skeleton" style={{ height: 220, borderRadius: 'var(--radius-md)', marginBottom: 16 }} aria-label="Loading project" />
    );
  }

  const initial = editId ? (detail ?? null) : null;

  if (accountId === undefined) {
    return <ProjectFormWithAccountPicker initial={initial} onDone={onDone} onCancel={onCancel} embedded={embedded} />;
  }
  // Keyed by the project being edited (or 'create') — without this, switching
  // editId while this instance stays mounted (e.g. a settings host that
  // re-renders the same <ProjectForm> for a different project) would reuse
  // ProjectFormInner's existing useState values instead of re-initializing
  // from the new `initial`, letting the previous project's edits leak onto
  // the new one on Save.
  return (
    <ProjectFormInner key={initial?.id ?? 'create'} accountId={accountId} initial={initial} onDone={onDone} onCancel={onCancel} embedded={embedded} />
  );
}
